import React, { useEffect, useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { Variants } from 'framer-motion'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import Lottie from 'react-lottie-player'

// Компоненты
import { SiteList } from '../../features/site-status/SiteList'
import { useSiteStatus } from '../../features/site-status/useSiteStatus'
import { LoadingSkeleton } from '../../shared/ui/LoadingSkeleton'
import { ErrorState } from '../../shared/ui/ErrorState'
import { Header } from '../../shared/layout/Header'
import { StatsOverview } from '../../features/stats/StatsOverview'
import { RealTimeClock } from '../../shared/ui/RealTimeClock'
import { StatusLegend } from '../../shared/ui/StatusLegend'
import { usePerformance } from '../../shared/hooks/usePerformance'
import { QuickActions } from '../../shared/ui/QuickActions'

// Production версия Lottie анимаций
const successAnimation = {
  v: "5.5.9",
  fr: 60,
  ip: 0,
  op: 60,
  w: 512,
  h: 512,
  nm: "Success",
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: "Checkmark",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 0, ix: 10 },
        p: { a: 0, k: [256, 256, 0], ix: 2 },
        a: { a: 0, k: [0, 0, 0], ix: 1 },
        s: { a: 0, k: [100, 100, 100], ix: 6 }
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              ind: 0,
              ty: "sh",
              ix: 1,
              ks: {
                a: 0,
                k: {
                  i: [[0, 0], [0, 0], [0, 0]],
                  o: [[0, 0], [0, 0], [0, 0]],
                  v: [[-40, -20], [0, 40], [60, -60]],
                  c: false
                },
                ix: 2
              },
              nm: "Path 1",
              mn: "ADBE Vector Shape - Group",
              hd: false
            },
            {
              ty: "fl",
              c: { a: 0, k: [0.2588235294, 0.6470588235, 0.9607843137, 1], ix: 4 },
              o: { a: 0, k: 100, ix: 5 },
              r: 1,
              bm: 0,
              nm: "Fill 1",
              mn: "ADBE Vector Graphic - Fill",
              hd: false
            },
            {
              ty: "tr",
              p: { a: 0, k: [0, 0], ix: 2 },
              a: { a: 0, k: [0, 0], ix: 1 },
              s: { a: 0, k: [100, 100], ix: 3 },
              r: { a: 0, k: 0, ix: 6 },
              o: { a: 0, k: 100, ix: 7 },
              sk: { a: 0, k: 0, ix: 4 },
              sa: { a: 0, k: 0, ix: 5 },
              nm: "Transform"
            }
          ],
          nm: "Group 1",
          np: 3,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false
        }
      ],
      ip: 0,
      op: 60,
      st: 0,
      bm: 0
    }
  ]
}

const errorAnimation = {
  v: "5.5.9",
  fr: 60,
  ip: 0,
  op: 60,
  w: 512,
  h: 512,
  nm: "Error",
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: "X Mark",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 0, k: 0, ix: 10 },
        p: { a: 0, k: [256, 256, 0], ix: 2 },
        a: { a: 0, k: [0, 0, 0], ix: 1 },
        s: { a: 0, k: [100, 100, 100], ix: 6 }
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              ind: 0,
              ty: "sh",
              ix: 1,
              ks: {
                a: 0,
                k: {
                  i: [[0, 0], [0, 0], [0, 0], [0, 0]],
                  o: [[0, 0], [0, 0], [0, 0], [0, 0]],
                  v: [[-40, -40], [40, 40], [-40, 40], [40, -40]],
                  c: false
                },
                ix: 2
              },
              nm: "Path 1",
              mn: "ADBE Vector Shape - Group",
              hd: false
            },
            {
              ty: "fl",
              c: { a: 0, k: [0.9607843137, 0.2588235294, 0.2588235294, 1], ix: 4 },
              o: { a: 0, k: 100, ix: 5 },
              r: 1,
              bm: 0,
              nm: "Fill 1",
              mn: "ADBE Vector Graphic - Fill",
              hd: false
            },
            {
              ty: "tr",
              p: { a: 0, k: [0, 0], ix: 2 },
              a: { a: 0, k: [0, 0], ix: 1 },
              s: { a: 0, k: [100, 100], ix: 3 },
              r: { a: 0, k: 0, ix: 6 },
              o: { a: 0, k: 100, ix: 7 },
              sk: { a: 0, k: 0, ix: 4 },
              sa: { a: 0, k: 0, ix: 5 },
              nm: "Transform"
            }
          ],
          nm: "Group 1",
          np: 3,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false
        }
      ],
      ip: 0,
      op: 60,
      st: 0,
      bm: 0
    }
  ]
}

// Типы для анимаций
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
      duration: 0.6
    }
  }
}

const itemVariants: Variants = {
  hidden: { 
    y: 30, 
    opacity: 0,
    scale: 0.95 
  },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      mass: 0.8
    }
  }
}

export const ExecutiveDashboardPage: React.FC = () => {
  const { sites, stats, isLoading, error, lastUpdate, refetch } = useSiteStatus()
  const { memoryUsage, fps } = usePerformance()
  
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('')
  const [animationComplete, setAnimationComplete] = useState(false)
  const [lottieAnim, setLottieAnim] = useState<typeof successAnimation | null>(null)
  const [activeMetric, setActiveMetric] = useState<'working' | 'problem' | 'not_working'>('working')
  
  const timelineRef = useRef<gsap.core.Timeline | null>(null)

  // GSAP анимация загрузки с улучшениями
  useGSAP(() => {
    if (!isLoading && !error && !animationComplete) {
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out",
          duration: 0.8
        }
      })
      
      // Фоновая анимация
      tl.fromTo('.background-gradient',
        { scale: 0.8, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.5 },
        0
      )
      
      // Карточки статистики
      tl.fromTo('.stat-card',
        { 
          scale: 0.9, 
          opacity: 0,
          y: 50,
          rotationX: -15 
        },
        { 
          scale: 1, 
          opacity: 1, 
          y: 0,
          rotationX: 0,
          stagger: 0.15,
          duration: 0.7,
          ease: "back.out(1.7)"
        },
        0.2
      )
      
      // Контент
      tl.fromTo('.main-content',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6 },
        0.4
      )
      
      // Сайты
      tl.fromTo('.site-item',
        { 
          x: -30, 
          opacity: 0,
          scale: 0.95 
        },
        { 
          x: 0, 
          opacity: 1, 
          scale: 1,
          stagger: 0.05,
          duration: 0.5,
          ease: "elastic.out(1, 0.5)"
        },
        0.6
      )
      
      timelineRef.current = tl
      tl.then(() => setAnimationComplete(true))
    }
    
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill()
      }
    }
  }, [isLoading, error, animationComplete])

  // Анимация времени обновления
  useEffect(() => {
    if (!lastUpdate) return

    const updateTime = () => {
      const now = new Date()
      const last = new Date(lastUpdate)
      const diffMs = now.getTime() - last.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      
      let newTime = ''
      if (diffMins < 1) {
        newTime = 'Только что'
      } else if (diffMins < 60) {
        newTime = `${diffMins} мин. назад`
      } else if (diffMins < 1440) {
        newTime = `${Math.floor(diffMins / 60)} ч. назад`
      } else {
        newTime = `${Math.floor(diffMins / 1440)} дн. назад`
      }

      // GSAP анимация
      gsap.to('.last-update-text', {
        opacity: 0,
        duration: 0.2,
        onComplete: () => {
          setLastUpdateTime(newTime)
          gsap.to('.last-update-text', {
            opacity: 1,
            duration: 0.3,
            scale: 1.05,
            yoyo: true,
            repeat: 1,
            ease: "power2.out"
          })
        }
      })
    }

    updateTime()
    const interval = setInterval(updateTime, 30000)
    
    return () => clearInterval(interval)
  }, [lastUpdate])

  // Lottie анимация для состояний
  useEffect(() => {
    if (error) {
      setLottieAnim(errorAnimation)
    } else if (!isLoading && sites.length > 0) {
      setLottieAnim(successAnimation)
    }
  }, [error, isLoading, sites])

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    
    try {
      await refetch()
      
      // Анимация успешного обновления
      gsap.to('.refresh-button', {
        rotate: 360,
        duration: 0.6,
        ease: "power2.out"
      })
      
      // Показать анимацию успеха
      setLottieAnim(successAnimation)
      
    } catch {
      setLottieAnim(errorAnimation)
    } finally {
      setTimeout(() => {
        setIsRefreshing(false)
        gsap.to('.refresh-button', { 
          rotate: 0, 
          duration: 0.3,
          ease: "power2.out"
        })
      }, 1000)
    }
  }, [refetch])

  // Функция для обработки клика по метрике (используется в UI)
  const handleMetricClick = useCallback((metric: 'working' | 'problem' | 'not_working') => {
    setActiveMetric(metric)
    
    // Анимация перехода
    gsap.to('.metric-highlight', {
      x: metric === 'working' ? 0 : metric === 'problem' ? '100%' : '200%',
      duration: 0.4,
      ease: "power2.out"
    })
  }, [])

  // Используем handleMetricClick для интерактивности
  useEffect(() => {
    // Пример: автоматическое переключение метрик каждые 5 секунд для демонстрации
    const interval = setInterval(() => {
      const metrics: ('working' | 'problem' | 'not_working')[] = ['working', 'problem', 'not_working']
      const currentIndex = metrics.indexOf(activeMetric)
      const nextIndex = (currentIndex + 1) % metrics.length
      handleMetricClick(metrics[nextIndex])
    }, 5000)

    return () => clearInterval(interval)
  }, [activeMetric, handleMetricClick])

  // Skeleton loading
  if (isLoading && !animationComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950">
        <LoadingSkeleton type="dashboard" />
      </div>
    )
  }

  // Error state с анимацией
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950">
        <ErrorState error={error} onRetry={handleRefresh} />
      </div>
    )
  }

  // Подготовка заголовка для Header
  const headerTitle = (
    <div className="flex items-center gap-3">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex-shrink-0 flex items-center justify-center shadow-lg"
      >
        <div className="text-white text-lg">🔍</div>
      </motion.div>
      <div className="min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1">
          <h1 className="text-base sm:text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
            Система мониторинга
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate hidden xs:block">
            Панель управления для руководства
          </p>
        </div>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate xs:hidden mt-0.5">
          Панель управления
        </p>
      </div>
    </div>
  )

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50/50 to-gray-100 dark:from-gray-900 dark:via-gray-900/50 dark:to-gray-950 overflow-hidden relative"
    >
      {/* Анимированный фон */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-float background-gradient" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-full blur-2xl animate-float delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_center,var(--tw-gradient-stops))] from-blue-500/5 via-transparent to-transparent" />
      </div>

      {/* Header */}
      <Header
        title={headerTitle}
        lastUpdate={lastUpdateTime}
        isRefreshing={isRefreshing}
        onRefresh={handleRefresh}
      />

      {/* Main content */}
      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 z-10 main-content">
        {/* Кнопки выбора метрики */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => handleMetricClick('working')}
            className={`px-4 py-2 rounded-lg transition-all ${activeMetric === 'working' 
              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium' 
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            Работают
          </button>
          <button
            onClick={() => handleMetricClick('problem')}
            className={`px-4 py-2 rounded-lg transition-all ${activeMetric === 'problem' 
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium' 
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            Проблемы
          </button>
          <button
            onClick={() => handleMetricClick('not_working')}
            className={`px-4 py-2 rounded-lg transition-all ${activeMetric === 'not_working' 
              ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 font-medium' 
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            Не работают
          </button>
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Sites list */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-2"
          >
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group relative border border-white/20 dark:border-gray-700/30">
              {/* Card header */}
              <div className="relative px-6 py-5 border-b border-white/20 dark:border-gray-700/30 bg-gradient-to-r from-white/60 to-white/40 dark:from-gray-800/60 dark:to-gray-800/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      className="p-3 rounded-lg bg-gradient-to-brrom-blue-500/20 to-blue-600/20 backdrop-blur-sm shadow-sm"
                    >
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        className="text-blue-500 text-xl"
                      >
                        🌐
                      </motion.div>
                    </motion.div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        Отслеживаемые сайты
                      </h2>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Всего {sites.length} • Доступность {stats.uptime}%
                        </p>
                        <div className="relative">
                          <div className="absolute inset-0 rounded-full animate-ping opacity-75 bg-emerald-500" />
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick stats */}
                  <div className="hidden md:flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {stats.uptime}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Uptime
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Shimmer effect */}
                <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-rrom-transparent via-white/30 to-transparent" />
              </div>

              {/* Sites list */}
              <div className="p-4 sm:p-6">
                <SiteList sites={sites} />
              </div>
              
              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-32 h-32 -translate-y-1/2 translate-x-1/2">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-500/10 to-purple-500/10 blur-2xl" />
              </div>
            </div>
          </motion.div>

          {/* Right column - Side panels */}
          <motion.div
            variants={itemVariants}
            className="space-y-6"
          >
            {/* Real-time clock */}
            <RealTimeClock 
              lastUpdate={lastUpdate}
              timeFormat="ru-RU"
              withSeconds
            />

            {/* Status legend */}
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl p-6 border border-white/20 dark:border-gray-700/30">
              <StatusLegend />
            </div>

            {/* Quick actions */}
            <QuickActions
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
            />

            {/* Animated chart мини */}
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl p-6 border border-white/20 dark:border-gray-700/30">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Статистика доступности
              </h3>
              <div className="h-[200px] animate-pulse bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-2xl" />
            </div>
          </motion.div>
        </div>

        {/* Stats overview */}
        <motion.div
          variants={itemVariants}
          className="mt-8"
        >
          <StatsOverview 
            stats={stats} 
            sites={sites} 
            activeMetric={activeMetric}
          />
        </motion.div>

        {/* Footer */}
        <motion.footer
          variants={itemVariants}
          className="mt-10 pt-6 border-t border-gray-200/30 dark:border-gray-700/30"
        >
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-3">
              <motion.span 
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-full animate-ping opacity-75 bg-emerald-500" />
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                </div>
                <span>Система мониторинга доступности веб-ресурсов</span>
              </motion.span>
              
              <span className="hidden sm:inline text-gray-400">•</span>
              
              <motion.span
                whileHover={{ scale: 1.05 }}
              >
                Для внутреннего использования руководством компании
              </motion.span>
              
              <span className="hidden sm:inline text-gray-400">•</span>
              
              <motion.span
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-2"
              >
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span>Данные обновляются в реальном времени</span>
              </motion.span>
            </div>
            
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-500 flex flex-col sm:flex-row items-center justify-center gap-2">
              <span>Последнее обновление:</span>
              <motion.span 
                className="last-update-text font-medium text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/50 backdrop-blur-sm"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {lastUpdateTime}
              </motion.span>
            </div>
          </div>
        </motion.footer>
      </main>

      {/* Lottie success animation */}
      <AnimatePresence>
        {lottieAnim && !isLoading && !error && (
          <motion.div
            initial={{ opacity: 0, scale: 0, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-50 pointer-events-none"
          >
            <Lottie
              animationData={lottieAnim}
              play
              loop={false}
              onComplete={() => setLottieAnim(null)}
              style={{ width: 80, height: 80 }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Performance overlay (только для разработки) */}
      {process.env.NODE_ENV === 'development' && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="fixed bottom-4 left-4 z-40 text-xs bg-black/80 backdrop-blur-sm text-white px-3 py-2 rounded-lg shadow-lg"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>FPS: {fps}</span>
            </div>
            <div className="w-px h-4 bg-white/30" />
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <span>Mem: {memoryUsage}MB</span>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}